'use server'

import { apiRequest, ApiResponse } from '../request'

const BASE = 'addresses'

export type AddressOwnerType = 'PARTNER' | 'PROFESSIONAL' | 'HOSPITAL'

export interface Address {
    id: number
    /** "Unidade Centro" — o que distingue as unidades na hora de escolher. */
    label?: string
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    cityCode?: string
    state?: string
    zipCode?: string
    country?: string
    /** Pré-selecionado no agendamento. */
    isPrimary: boolean
    sortOrder: number
    ownerType: AddressOwnerType
    ownerId: number
}

/** Exatamente um dos ids deve ser informado. */
export interface AddressOwner {
    partnerId?: number
    professionalId?: number
    hospitalId?: number
}

export type CreateAddressData = AddressOwner &
    Partial<Omit<Address, 'id' | 'ownerType' | 'ownerId'>>

export type UpdateAddressData = Partial<
    Omit<Address, 'id' | 'ownerType' | 'ownerId'>
>

export async function getAddresses(
    owner: AddressOwner
): Promise<ApiResponse<Address[]>> {
    return apiRequest<Address[]>(BASE, { params: owner })
}

/**
 * Endereços das três origens numa chamada só.
 *
 * A confirmação de local oferece parceiro, profissional e hospital lado a lado;
 * buscar cada um separadamente fazia a tela esperar três idas ao servidor para
 * desenhar uma lista.
 */
export async function getAppointmentAddresses(owner: AddressOwner): Promise<
    ApiResponse<{
        partner: Address[]
        professional: Address[]
        hospital: Address[]
    }>
> {
    return apiRequest(`${BASE}/for-appointment`, { params: owner })
}

export async function createAddress(
    data: CreateAddressData
): Promise<ApiResponse<Address>> {
    return apiRequest<Address>(BASE, { data }, 'post')
}

/**
 * Altera o endereço. Vale para os próximos atendimentos daquela unidade — os
 * agendamentos já confirmados guardam o endereço congelado no próprio registro.
 */
export async function updateAddress(
    id: number,
    data: UpdateAddressData
): Promise<ApiResponse<Address>> {
    return apiRequest<Address>(`${BASE}/${id}`, { data }, 'patch')
}

export async function setPrimaryAddress(
    id: number
): Promise<ApiResponse<Address>> {
    return apiRequest<Address>(`${BASE}/${id}/primary`, {}, 'patch')
}

export async function deleteAddress(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`${BASE}/${id}`, {}, 'delete')
}
